import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import { MVP_NODES } from "@music-app/learning-system";
import { usePrefsStore } from "@music-app/persistence";
import { hasLessonForNode } from "@music-app/lessons/runtime";

interface IntroducingBannerProps {
  nodeIds: string[];
}

export function IntroducingBanner({ nodeIds }: IntroducingBannerProps): ReactNode {
  const isLessonCompleted = usePrefsStore((s) => s.isLessonCompleted);

  if (nodeIds.length === 0) return null;

  return (
    <div
      className="border border-accent/30 bg-accent/5 p-4 mb-4"
      role="region"
      aria-label="New skills introduced this session"
    >
      <p className="font-mono text-[10px] uppercase tracking-widest text-accent mb-3">
        introducing
      </p>
      <ul className="space-y-3">
        {nodeIds.map((nodeId) => {
          const node = MVP_NODES.find((n) => n.id === nodeId);
          if (!node) return null;
          const hasLesson = hasLessonForNode(nodeId);
          const lessonDone = isLessonCompleted(nodeId);
          return (
            <li key={nodeId} className="flex items-center justify-between gap-4 flex-wrap">
              <span className="font-serif italic text-ink">{node.title}</span>
              {hasLesson && !lessonDone && (
                <Link
                  to={`/learn/${nodeId}`}
                  className="flex-shrink-0 px-3 py-1 bg-accent text-paper text-xs font-mono hover:bg-ink transition-colors"
                >
                  Read the lesson first →
                </Link>
              )}
              {hasLesson && lessonDone && (
                <Link
                  to={`/learn/${nodeId}`}
                  className="flex-shrink-0 font-mono text-[10px] uppercase tracking-widest text-inkFaded hover:text-ink"
                >
                  Lesson ✓
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
